import moment from 'moment';
import type Moment from 'moment';

/**
 * This function takes a time value and will then give all time values for the
 * given day, in 15 minute increments.
 *
 * @param {Moment} time The selected time. Get all time values for this day.
 * @param {string} maxTime The maximum time to show. If this is provided, we will not show any times beyond this time.
 * @param {string} minTime The minimum time to show. If this is provided, we will not show any times before this time.
 * @param {boolean} allowPastTimes If true, allow times before the current time.
 * @param {Moment[]} additionalTimes Additional times to show. These will be added to the list of times.
 */
export function getSingleDayTimeOptions(
    // @ts-expect-error
    time: Moment,
    maxTime?: string,
    minTime?: string,
    allowPastTimes = false,
    // @ts-expect-error
    additionalTimes?: Moment[]
): {
    // @ts-expect-error
    value: Moment;
    label: string;
}[] {
    const currentTime = moment();
    const endOfDay = moment(time).endOf('day');

    // Kind of a confusing name, but I can't come up with anything better. This
    // takes the selected time and gets the start of that day.
    let incrementer = moment(time).startOf('day');
    // @ts-expect-error
    let values = [];

    if (endOfDay.isBefore(currentTime) && !allowPastTimes) {
        // If the day to get times for is before the current time, we wouldn't
        // show any times so we should just short circuit this
        return [];
    }
    while (incrementer.isBefore(endOfDay)) {
        if (!allowPastTimes && incrementer.isBefore(currentTime)) {
            incrementer = incrementer.add(15, 'm');

            continue;
        }

        if (maxTime || minTime) {
            const formattedTime = incrementer.format('HH:mm');
            if ((maxTime && formattedTime > maxTime) || (minTime && formattedTime < minTime)) {
                incrementer = incrementer.add(15, 'm');

                continue;
            }
        }

        // @ts-expect-error
        values = values.concat({value: incrementer, label: incrementer.format('h:mm A')});
        incrementer = moment(incrementer).add(15, 'm');
    }
    if (additionalTimes) {
        // @ts-expect-error
        additionalTimes.forEach((additionalTime: Moment) => {
            const timeStr = additionalTime.format('h:mm A');
            // @ts-expect-error
            if (!values.some((v) => v.label === timeStr)) {
                // @ts-expect-error
                values = values.concat({value: additionalTime, label: timeStr});
            }
        });

        values.sort((a, b) => a.value.valueOf() - b.value.valueOf());
    }

    return values;
}

/**
 * This function takes a time value and will then give all time values for the
 * given day, in 15 minute increments. If a baseline time is provided, it will
 * dynamically provide times based around whether the selected time is on the
 * same day as the baseline time.
 *
 * @param {Moment} time The selected time. Get all time values for this day.
 * @param {Moment} baselineTime The reference time in which the picker determines multi-day behavior.
 * @param {string} maxTime The maximum time to show. If this is provided, we will not show any times beyond this time.
 * @param {string} minTime The minimum time to show. If this is provided, we will not show any times before this time.
 * @param {boolean} allowPastTimes If true, allow times before the current time.
 * @param {Moment[]} additionalTimes Additional times to show. These will be added to the list of times.
 */
export function getTimeOptions(
    // @ts-expect-error
    time: Moment,
    // @ts-expect-error
    baselineTime?: Moment,
    maxTime?: string,
    minTime?: string,
    allowPastTimes = false,
    // @ts-expect-error
    additionalTimes?: Moment[]
): {
    // @ts-expect-error
    value: Moment;
    label: string;
}[] {
    const currentTime = moment();

    // If the selected time is not on the same day as the baseline time, we need
    // to display all options for the new selected day instead
    if (!isJudgedFromBaselineTime(baselineTime, time) || !baselineTime) {
        return getSingleDayTimeOptions(time, maxTime, minTime, allowPastTimes, additionalTimes);
    }

    const endOfWindow = moment(baselineTime).clone().add(24, 'hours');

    let incrementer = moment(roundTimeToNextQuarter(baselineTime));
    // @ts-expect-error
    let values = [];

    if (endOfWindow.isBefore(currentTime) && !allowPastTimes) {
        // If the day to get times for is before the current time, we wouldn't
        // show any times so we should just short circuit this
        return [];
    }
    while (incrementer.isBefore(endOfWindow)) {
        if (!allowPastTimes && incrementer.isBefore(currentTime)) {
            incrementer = incrementer.add(15, 'm');

            continue;
        }

        // @ts-expect-error
        values = values.concat({value: incrementer, label: incrementer.format('h:mm A')});
        incrementer = moment(incrementer).add(15, 'm');
    }
    if (additionalTimes) {
        // @ts-expect-error
        additionalTimes.forEach((additionalTime: Moment) => {
            const timeStr = additionalTime.format('h:mm A');
            // @ts-expect-error
            if (!values.some((v) => v.label === timeStr)) {
                // @ts-expect-error
                values = values.concat({value: additionalTime, label: timeStr});
            }
        });

        values.sort((a, b) => a.value.valueOf() - b.value.valueOf());
    }

    return values;
}

// @ts-expect-error
export function getDurationText(baseTime: Moment | null | undefined, time: Moment): string {
    if (!baseTime) {
        return '';
    }

    if (!moment.isMoment(time)) {
        return '';
    }

    const startTime = roundTimeToClosestQuarter(baseTime);

    let duration = Math.round(time.diff(baseTime, 'minutes', true));

    // If we're close enough to 60 minutes to round to it, we'll swap to showing
    // (1 hr) to begin the rounding estimates
    if (Math.round(duration / 15) * 15 === 60) {
        duration = 60;
    }

    let durationText = '';
    if (duration < 60) {
        durationText = `(${duration} min)`;
    } else {
        const durationHours = Math.round(time.diff(startTime, 'hours', true) * 4) / 4;
        durationText = `(${durationHours} ${durationHours === 1 ? 'hr' : 'hrs'})`;
    }

    return durationText;
}

export function isJudgedFromBaselineTime(
    // @ts-expect-error
    baselineTime: Moment | null | undefined,
    // @ts-expect-error
    time: Moment
): boolean {
    return Boolean(baselineTime && time.isSame(baselineTime, 'day'));
}

// @ts-expect-error
export function roundTimeToClosestQuarter(time: Moment): Moment {
    const timeCopy = moment(time);
    const minutes = timeCopy.minutes();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    timeCopy.minutes(roundedMinutes);

    return timeCopy;
}

/**
 * This function can be a bit confusing, but this takes a given time and returns
 * the next 15 minute interval on the hour. For example, 1:13 would return 1:15.
 * 1:46 would return 2:00.
 *
 * @param {Moment} time The current selected time
 */
// @ts-expect-error
export function roundTimeToNextQuarter(time: Moment): Moment {
    const timeCopy = moment(time);
    // Get the number of 15 minute intervals on this time
    let intervals = Math.floor(timeCopy.minutes() / 15);
    // If there are any extra minutes on the time, increment intervals
    if (timeCopy.minutes() % 15 !== 0) intervals++;
    // If there are 4 intervals, add a whole hour and don't add any minutes
    if (intervals === 4) {
        timeCopy.add(1, 'hours');
        intervals = 0;
    }
    // Add the number of intervals times 15
    timeCopy.minutes(intervals * 15);
    timeCopy.seconds(0);

    return timeCopy;
}

const PARSABLE_TIME_FORMATS = [
    'h:mm A', // 11:11 PM
    'h:mm a', // 11:11 pm
    'h:mma', // 11:11pm
    'h:mmA', // 11:11PM
    'H:mm', // 14:30 (24-hour format)
    'h A', // 11 PM
    'h a', // 11 pm
    'ha', // 11pm
    'hA', // 11PM
    'H', // 14 (24-hour format)
];

/**
 * Parse a time string (e.g., "11:11 PM", "2:30 AM", "14:30") into a moment
 * object.
 *
 * @param {string} timeString The time string to parse
 * @param {Moment} referenceDate The date to use for creation. If not provided, use current date
 * @returns {Moment|null} The parsed moment object or null if invalid
 */
// @ts-expect-error
export function parseTimeString(timeString: string, referenceDate?: Moment): Moment | null {
    if (!timeString || !timeString.trim()) {
        return null;
    }

    const trimmed = timeString.trim();

    for (const format of PARSABLE_TIME_FORMATS) {
        const parsed = moment(trimmed, format, true);
        if (parsed.isValid()) {
            // Use the reference time's date but the parsed time
            const result = moment(referenceDate).set({
                hour: parsed.hour(),
                minute: parsed.minute(),
                second: 0,
                millisecond: 0,
            });

            return result;
        }
    }

    return null;
}

/**
 * Validate if a time string is in a valid format
 *
 * @param {string} timeString The time string to validate
 * @returns {boolean} True if the time string is valid
 */
export function isValidTimeString(timeString: string): boolean {
    if (!timeString || !timeString.trim()) {
        return false;
    }

    const parsed = parseTimeString(timeString);

    return parsed !== null;
}
